/**
 * Why an MCP request was denied, and the words for it.
 *
 * Three gates can deny the same request — hub's checkMcpAccess (webapp consent),
 * the api's mcpAuth, and the mcp-server's own mirror — and which one fires first
 * depends on cache freshness, not on what the user did. They return a reason;
 * the copy lives only here, so all three say the same thing.
 */

export const MCP_DENIAL_REASONS = [
  "slackbot-mcp-disabled",
  "mcp-disabled",
  "mcp-unavailable",
  "org-uninstalled",
  "slack-access-revoked",
  "user-removed",
  "user_not_found",
  "org_not_found",
  "results-client-disabled",
  "client-disabled",
  "client-not-found",
  "client-unverifiable",
  "unavailable",
] as const;

export type McpDenialReason = (typeof MCP_DENIAL_REASONS)[number];

export interface McpDenialCopy {
  /** Read by the AI host, which relays it to whoever asked. */
  host: string;
  /** Read by a person directly: the card, the consent page. */
  human: string;
  /** False when nothing about the call can change the answer. */
  retryable: boolean;
}

/** Appended to host copy for denials no retry can clear. */
export const MCP_DENIAL_NO_RETRY = "Retrying will not help until that changes.";

export const MCP_DENIAL_COPY: Record<McpDenialReason, McpDenialCopy> = {
  "slackbot-mcp-disabled": {
    host: "Polly's Slackbot AI tools are turned off for this workspace. A Polly admin can enable them under Admin → Settings.",
    human:
      "Polly's Slackbot AI tools are turned off for this workspace. A Polly admin can enable them under Admin → Settings.",
    retryable: false,
  },
  "mcp-disabled": {
    host: "Polly's AI tools connection is turned off for this workspace. Ask a Polly admin to turn on the AI tools connection in Polly's web dashboard under Admin → Settings, then reconnect from your AI client.",
    human:
      "Polly's AI tools connection is turned off for this workspace. Ask a Polly admin to turn it on in Polly's web dashboard under Admin → Settings, then try connecting again.",
    retryable: false,
  },
  "mcp-unavailable": {
    host: "Polly's AI tools aren't included in this workspace's plan. Ask a Polly admin to upgrade the plan, or contact support@polly.ai.",
    human:
      "Polly's AI tools aren't included in this workspace's plan. Ask a Polly admin to upgrade the plan, or contact support@polly.ai.",
    retryable: false,
  },
  "org-uninstalled": {
    host: "Polly is no longer installed in this workspace, so its AI tools are unavailable. Reinstall Polly to continue.",
    human:
      "Polly is no longer installed in this workspace, so its AI tools are unavailable. Reinstall Polly to continue.",
    retryable: false,
  },
  "slack-access-revoked": {
    host: "Polly's access to this Slack workspace was revoked. A Polly admin needs to reconnect Polly to Slack.",
    human:
      "Polly's access to this Slack workspace was revoked. A Polly admin needs to reconnect Polly to Slack.",
    retryable: false,
  },
  "user-removed": {
    host: "Your Polly account is no longer active in this workspace. Contact your Polly admin.",
    human:
      "Your Polly account is no longer active in this workspace. Contact your Polly admin.",
    retryable: false,
  },
  user_not_found: {
    host: "You don't have a Polly account in this workspace yet. Install Polly in this workspace to get started.",
    human:
      "You don't have a Polly account in this workspace yet. Install Polly in this workspace to get started.",
    retryable: false,
  },
  org_not_found: {
    host: "You don't have a Polly account in this workspace yet. Install Polly in this workspace to get started.",
    human:
      "You don't have a Polly account in this workspace yet. Install Polly in this workspace to get started.",
    retryable: false,
  },
  "results-client-disabled": {
    host: "Results access is turned off for this connection. A Polly admin controls it under Admin → AI Features.",
    human:
      "Results access is turned off for this connection. A Polly admin can enable it under Admin → AI Features.",
    retryable: false,
  },
  "client-disabled": {
    host: "This AI connection has been disabled. A Polly admin can re-enable it under Admin → AI Features.",
    human:
      "This AI connection has been disabled. A Polly admin can re-enable it under Admin → AI Features.",
    retryable: false,
  },
  "client-not-found": {
    host: "This AI connection is no longer registered with Polly. Ask the user to reconnect Polly from their AI client.",
    human:
      "This AI connection is no longer registered with Polly. Reconnect Polly from your AI client.",
    retryable: false,
  },
  "client-unverifiable": {
    host: "Polly could not verify this AI connection just now. Wait a moment and try again.",
    human:
      "Polly could not verify this AI connection just now. Try again in a moment.",
    retryable: true,
  },
  unavailable: {
    host: "Polly could not authorize this request. Contact your Polly admin if this persists.",
    human:
      "Polly could not authorize this request. Contact your Polly admin if this persists.",
    retryable: false,
  },
};

/** What a denial looks like on the wire, api → mcp-server. */
export const MCP_DENIAL_ERROR = "mcp_access_denied";

export interface McpDenialEnvelope {
  ok: false;
  error: typeof MCP_DENIAL_ERROR;
  reason: McpDenialReason;
  message: string;
}

/** Reasons cross service boundaries, so they arrive unvalidated. */
export function toMcpDenialReason(value: unknown): McpDenialReason {
  return MCP_DENIAL_REASONS.includes(value as McpDenialReason)
    ? (value as McpDenialReason)
    : "unavailable";
}

export function mcpDenialHostMessage(reason: McpDenialReason): string {
  const copy = MCP_DENIAL_COPY[reason];
  return copy.retryable ? copy.host : `${copy.host} ${MCP_DENIAL_NO_RETRY}`;
}

export function mcpDenialHumanMessage(reason: McpDenialReason): string {
  return MCP_DENIAL_COPY[reason].human;
}
